<template>
	<div class="grid-flex-container">
		<WorkbookReportPreviewToolbar
			:reportName="reportName"
			:hasFilters="hasFilters"
			:quickFilter="quickFilter"
			:advancedFilter="advancedFilter"
			:visibleColumns="filterColumns"
			:advancedFiltersCount="advancedFiltersCount"
			@quickFilterChanged="onQuickFilterChanged"
			@advancedFilterChanged="onAdvancedFilterChanged"
		/>

		<div class="generic-grid-container" v-if="isRawData">
			<grid-view
				v-if="visibleColumns"
				:gridData="gridData"
				:irisUserMetadata="irisUserMetadata"
				:loadingData="loadingData"
				:visibleColumns="visibleColumns"
				:sorting="sorting"
				:page="page"
				:rowsPerPage="rowsPerPage"
				:totalCount="totalCount"
				:hasActions="false"
				@rowsPerPageChanged="onRowsPerPageChanged"
				@pageChanged="onPageChanged"
				@sortChanged="onSortChanged"
			>
				<template v-slot:[`actionsColumns`]> </template>
			</grid-view>
		</div>

		<div class="generic-grid-container" v-else>
			<v-data-table
				:footer-props="{
					itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
					showFirstLastPage: true,
					showCurrentPage: true,
					pagination: {
						page,
						itemsPerPage: rowsPerPage,
						pageStart,
						pageStop,
						pageCount,
						itemsLength: totalCount,
					},
				}"
				:options.sync="options"
				:server-items-length="totalCount"
				hide-default-header
				:hide-default-footer="hideFooter"
				fixed-header
				dense
			>
				<template #header>
					<thead class="v-data-table-header">
						<tr>
							<th
								v-for="(header, index) in visibleColumns"
								:key="index"
								@click="sortItems(header.Id)"
								class="active"
							>
								<span v-if="!getSortIndex(header.Id)">{{
									header.Header
								}}</span>
								<span v-else style="color: black">{{
									header.Header
								}}</span>
								<v-icon
									v-if="showAsc(header.Id)"
									dense
									color="black"
									style="font-size: 18px"
									>mdi-arrow-down</v-icon
								>
								<v-icon
									v-if="showDesc(header.Id)"
									dense
									color="black"
									style="font-size: 18px"
									>mdi-arrow-up</v-icon
								>
								<v-badge
									v-if="getSortIndex(header.Id)"
									:content="getSortIndex(header.Id)"
									inline
									color="#e0e0e0"
								></v-badge>
							</th>
						</tr>
						<tr v-if="loadingData" class="v-data-table__progress">
							<th :colspan="visibleColumns.length" class="column">
								<v-progress-linear
									indeterminate
								></v-progress-linear>
							</th>
						</tr>
					</thead>
				</template>
				<template #body>
					<tbody v-if="!loadingData">
						<tr
							class="v-data-table__empty-wrapper"
							v-if="gridData && gridData.length == 0"
						>
							<td :colspan="visibleColumns.length + 1">
								No matching records found
							</td>
						</tr>
						<tr v-for="item in gridData" :key="item.Id">
							<td
								v-for="column in visibleColumns"
								:key="column.FieldName"
							>
								{{ item[column.FieldName] }}
							</td>
						</tr>
					</tbody>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
import GridView from '../../shared/gridViews/GridView.vue';
import WorkbookReportPreviewToolbar from './WorkbookReportPreviewToolbar.vue';

export default {
	name: 'WorkbookReportPreview',
	components: { GridView, WorkbookReportPreviewToolbar },
	props: {
		filterColumns: Array,
		visibleColumns: Array,
		gridData: Array,
		irisUserMetadata: Array,
		loadingData: Boolean,
		page: Number,
		rowsPerPage: Number,
		totalCount: Number,
		quickFilter: Object,
		advancedFilter: Object,
		advancedFiltersCount: Number,
		sorting: Array,
		reportName: String,
		hasFilters: Boolean,
		isRawData: Boolean,
	},
	computed: {
		// the sorted columns are numbered in the header, in the order they are applied
		sortIndex() {
			if (!this.sorting) return [];
			return this.sorting.map((x, index) => ({
				FieldId: x.FieldId,
				Index: index + 1,
			}));
		},
		hideFooter() {
			if (this.loadingData) return true;
			if (this.gridData && this.gridData.length <= 0) return true;
			return false;
		},
		pageStart() {
			return this.rowsPerPage * (this.page - 1);
		},
		pageStop() {
			return this.rowsPerPage * this.page;
		},
		pageCount() {
			if (!this.rowsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(this.totalCount / this.rowsPerPage);
			let remainder = this.totalCount % this.rowsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},
		options: {
			get() {
				return {
					page: this.page,
					itemsPerPage: this.rowsPerPage,
				};
			},
			set(data) {
				if (this.page != data.page && !isNaN(data.page)) {
					this.onPageChanged(data.page);
				}

				if (
					this.rowsPerPage != data.itemsPerPage &&
					!isNaN(data.itemsPerPage)
				) {
					this.onRowsPerPageChanged(data.itemsPerPage);
				}
			},
		},
	},
	methods: {
		showDesc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) => column.FieldId === headerId && column.Operator === 1
			);
		},
		showAsc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) =>
					column.FieldId === headerId && column.Operator === -1
			);
		},
		// click cycles a column through ascending, descending and not sorted, like the custom data report grid
		sortItems(headerId) {
			let localSorting = this.sorting
				? JSON.parse(JSON.stringify(this.sorting))
				: [];
			let sorterIndex = localSorting.findIndex(
				(column) => column.FieldId === headerId
			);
			if (
				sorterIndex !== -1 &&
				localSorting[sorterIndex].Operator === -1
			) {
				localSorting.splice(sorterIndex, 1);
			} else if (
				sorterIndex !== -1 &&
				localSorting[sorterIndex].Operator === 1
			) {
				localSorting[sorterIndex].Operator = -1;
			} else {
				localSorting.push({ Operator: 1, FieldId: headerId });
			}
			this.onSortChanged(localSorting);
		},
		getSortIndex(fieldId) {
			let result = null;
			this.sortIndex.forEach((x) => {
				if (x.FieldId == fieldId) result = x.Index;
			});
			return result;
		},
		onQuickFilterChanged(e) {
			this.$emit('quickFilterChanged', e);
		},
		onAdvancedFilterChanged(e) {
			this.$emit('advancedFilterChanged', e);
		},
		onRowsPerPageChanged(e) {
			this.$emit('rowsPerPageChanged', e);
		},
		onPageChanged(e) {
			this.$emit('pageChanged', e);
		},
		onSortChanged(e) {
			this.$emit('sortChanged', e);
		},
	},
};
</script>
